import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import SearchInput from 'components/field/SearchInput';
import NoRecordsFound from 'components/global/NoRecordsFound';
import SubformHeader from 'components/global/SubformHeader';

import useDebounce from 'hooks/useDebounce';

import OnlineStoreCard from './OnlineStoreCard';
import * as S from './OnlineStores.styled';
import { getOnlineStoreList } from './onlineStoresSlice';

const useStoreSearch = () => {
	const { t } = useTranslation();
	const [searchValue, setSearchValue] = useState('');
	const debouncedValue = useDebounce(searchValue, 500);
	const OnlineStoreList = useSelector((state) => state?.onlineStores);

	const filteredStores = useMemo(() => {
		if (!debouncedValue) return OnlineStoreList;

		const searchTerm = debouncedValue.toLowerCase();
		return OnlineStoreList?.filter((store) => {
			const storeName = store.name.toLowerCase();
			const translatedName = t(`salesChannel.${store.integrationName}`).toLowerCase();

			return (
				storeName.includes(searchTerm) ||
				storeName.split(' ').join('').includes(searchTerm) ||
				translatedName.includes(searchTerm)
			);
		});
	}, [debouncedValue, OnlineStoreList]);

	return { searchValue, setSearchValue, filteredStores };
};

export const StoresSkeleton = () => (
	<S.StoresContainer>
		{[...Array(15)].map((_, i) => (
			<OnlineStoreCard key={i} skeleton={true} />
		))}
	</S.StoresContainer>
);

const OnlineStores = () => {
	const dispatch = useDispatch();
	const OnlineStoreList = useSelector((state) => state?.onlineStores);

	useEffect(() => {
		dispatch(getOnlineStoreList());
	}, []);

	const { searchValue, setSearchValue, filteredStores } = useStoreSearch();

	return (
		<S.FlexCard>
			<S.HeaderContainer>
				<SubformHeader
					boldTitle="connectYourOnlineStore"
					lightTitle="salesChannelDescription"
					className="table-lord-icon"
				/>

				<S.InputContainer>
					<SearchInput
						setValue={setSearchValue}
						value={searchValue}
						placeholder="searchSalesChannel"
						fullWidth
					/>
				</S.InputContainer>
			</S.HeaderContainer>

			{OnlineStoreList?.length ? (
				filteredStores?.length === 0 ? (
					<NoRecordsFound />
				) : (
					<S.StoresContainer>
						{filteredStores?.map((onlineStore) => (
							<OnlineStoreCard key={onlineStore.ID} store={onlineStore} />
						))}
					</S.StoresContainer>
				)
			) : (
				<StoresSkeleton />
			)}
		</S.FlexCard>
	);
};

export default OnlineStores;
